/**
 * makeThumbnail — generate a 256×256 JPEG thumbnail from a customer-
 * selected image File entirely in the browser, no backend round-trip.
 * Returns a Blob the caller can hand to fetch() as a PUT body.
 *
 * Centre-crops to square, then bilinear-downscales via Canvas. JPEG
 * quality 0.85 gives ~15-25 KB per thumb. Mirrors the backend
 * imgproc.MakeThumbnail output shape so direct + lazy fallback paths
 * produce visually-identical thumbs.
 *
 * Used by uploadFlow.ts — the browser already has the file bytes from
 * the customer's <input type=file>, so the second presigned PUT for
 * the thumb costs nothing extra. If thumb generation fails (HEIC,
 * corrupt JPEG, browser refuses to decode), we resolve null and the
 * upload still succeeds; backend's lazy-thumbnail path covers the gap
 * on first /thumbnail GET.
 */
const THUMB_SIZE = 256;
const JPEG_QUALITY = 0.85;

/**
 * Skip thumbnail generation when the original is already small enough
 * (≤256 KB) — the grid sidebar can render the original directly without
 * meaningful bandwidth cost, and we save the decode/encode CPU on the
 * main thread. The backend's lazy-thumbnail path covers the case where
 * a grid view ever explicitly wants the 256×256 cropped variant.
 */
const SKIP_THUMB_BYTES = 256 * 1024;

export async function makeThumbnailBlob(file: File): Promise<Blob | null> {
    if (file.size <= SKIP_THUMB_BYTES) {
        // Original is thumbnail-sized already. Return null so uploadFlow
        // skips the parallel PUT entirely — saves ~100-300ms per file.
        return null;
    }
    // Use createImageBitmap when available — fast off-thread decode,
    // honors EXIF orientation when {imageOrientation:'from-image'}.
    let bitmap: ImageBitmap | null = null;
    if (typeof createImageBitmap === 'function') {
        try {
            bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' } as ImageBitmapOptions);
        } catch {
            bitmap = null;
        }
    }

    let srcWidth: number;
    let srcHeight: number;
    let drawSource: CanvasImageSource;

    if (bitmap) {
        srcWidth = bitmap.width;
        srcHeight = bitmap.height;
        drawSource = bitmap;
    } else {
        // Fallback: HTMLImageElement via object URL.
        const url = URL.createObjectURL(file);
        try {
            const img = await loadImage(url);
            srcWidth = img.naturalWidth;
            srcHeight = img.naturalHeight;
            drawSource = img;
        } catch {
            URL.revokeObjectURL(url);
            return null;
        }
        // Revoke after canvas draw — but we need it through the draw,
        // so do it in a microtask after we've finished.
        queueMicrotask(() => URL.revokeObjectURL(url));
    }

    if (srcWidth === 0 || srcHeight === 0) {
        bitmap?.close();
        return null;
    }

    // Centre-crop to square, then downscale to 256×256.
    const side = Math.min(srcWidth, srcHeight);
    const sx = (srcWidth - side) / 2;
    const sy = (srcHeight - side) / 2;

    const canvas = document.createElement('canvas');
    canvas.width = THUMB_SIZE;
    canvas.height = THUMB_SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        bitmap?.close();
        return null;
    }
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(drawSource, sx, sy, side, side, 0, 0, THUMB_SIZE, THUMB_SIZE);
    bitmap?.close();

    return new Promise<Blob | null>((resolve) => {
        canvas.toBlob(
            (blob) => resolve(blob),
            'image/jpeg',
            JPEG_QUALITY,
        );
    });
}

function loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('image decode failed'));
        img.src = src;
    });
}
